import type { Bar, EngineState, Note, Variation } from '../types';
import { CHORD_MS, MIN_DELAY_SEC, NUM_BAR, NUM_VAR, clamp } from '../constants';
import type { Store } from '../state/store';

export interface PoppedEvent {
  note: Note;
  varIdx: number;
  barIdx: number;
  eventIdx: number;
  deltaSec: number; // time to wait BEFORE the next event (0 for chords / bar transitions)
  variationChanged: boolean; // this event is the last before a jump to a different variation
}

export class Engine {
  constructor(
    private readonly variations: Variation[],
    private readonly offsets: Bar[][],
    private readonly store: Store<EngineState>,
  ) {}

  private notes(varIdx: number): Note[] {
    return this.variations[varIdx]!.notes;
  }

  private bar(varIdx: number, barIdx: number): Bar {
    return this.offsets[varIdx]![barIdx]!;
  }

  reset(): void {
    const s = this.store.get();
    const first = this.bar(s.playVar, s.playBar).first;
    this.store.set({ playEvent: first, jumpVar: s.playVar, jumpBar: s.playBar });
  }

  pendingJump(): boolean {
    const s = this.store.get();
    return s.jumpBar !== s.playBar || s.jumpVar !== s.playVar;
  }

  requestJump(barIdx: number, varIdx: number): void {
    this.store.set({
      jumpBar: clamp(Math.floor(barIdx), 0, NUM_BAR - 1),
      jumpVar: clamp(Math.floor(varIdx), 0, NUM_VAR - 1),
    });
  }

  clearJump(): void {
    const s = this.store.get();
    this.store.set({ jumpBar: s.playBar, jumpVar: s.playVar });
  }

  setLoop(v: boolean): void {
    this.store.set({ loop: v });
  }
  setLinear(v: boolean): void {
    this.store.set({ linear: v });
  }
  setInvertPitch(v: boolean): void {
    this.store.set({ invertPitch: v });
  }
  setMotion(roll: number, pitch: number): void {
    this.store.set({ roll, pitch });
  }
  setPlaying(v: boolean): void {
    this.store.set({ isPlaying: v });
  }

  speedFromState(): number {
    const s = this.store.get();
    // Always modulate by roll: roll ∈ [-π/2, π/2] maps to speed ∈ [~0.4, ~2.5].
    // `invertPitch` reverses the direction (so right-tilt slows instead of speeds up).
    const r = s.roll / (Math.PI / 2);
    const signed = s.invertPitch ? -r : r;
    return clamp(1 + signed, 0.25, 4.0);
  }

  /**
   * Mirror of iOS GoldbergAudio.play() advance logic.
   * Returns the note that should be emitted now plus the wait BEFORE the next event.
   * Bar-end dispatch (jump/loop/linear/random) happens here, mirroring iOS.
   */
  popEvent(): PoppedEvent {
    const s = this.store.get();
    const playVar = s.playVar;
    const playBar = s.playBar;
    const playEvent = s.playEvent;
    const note = this.notes(playVar)[playEvent]!;
    const curBar = this.bar(playVar, playBar);
    const isLastInBar = playEvent >= curBar.last;
    const isChord = note.time <= CHORD_MS;

    let nextVar = playVar;
    let nextBar = playBar;
    let nextEvent = playEvent + 1;
    let deltaSec = 0;
    let variationChanged = false;

    if (isLastInBar) {
      // === iOS end-of-bar dispatch ===
      // First: variation jump.
      if (playVar !== s.jumpVar) {
        nextVar = s.jumpVar;
      } else if (!s.linear) {
        nextVar = Math.floor(Math.random() * NUM_VAR);
      }
      // Then: bar advance / loop / bar jump.
      if (!s.loop) {
        if (playBar !== s.jumpBar) {
          nextBar = s.jumpBar;
        } else {
          if (playBar === NUM_BAR - 1) {
            nextVar = (nextVar + 1) % NUM_VAR;
          }
          nextBar = (playBar + 1) % NUM_BAR;
        }
      } else if (playBar !== s.jumpBar) {
        nextBar = s.jumpBar;
      }
      variationChanged = nextVar !== playVar;
      // Clear pending jump (mirror iOS: jump* := play* after dispatch).
      const firstOfNextBar = this.bar(nextVar, nextBar).first;
      this.store.set({
        playVar: nextVar,
        playBar: nextBar,
        playEvent: firstOfNextBar,
        jumpVar: nextVar,
        jumpBar: nextBar,
      });
      // Preserve the last note's `time` field as the gap to the next bar's
      // first event. iOS discards this and recurses immediately, but that
      // collapses bars whose last event has a real (non-chord) time value —
      // e.g. var 9 bar 22 ends with D#2 time=248, which is the actual gap to
      // the next downbeat.
      if (isChord) {
        deltaSec = 0;
      } else {
        const speed = this.speedFromState();
        deltaSec = Math.max((note.time * 0.001) / speed, MIN_DELAY_SEC);
      }
    } else {
      if (isChord) {
        deltaSec = 0;
      } else {
        const speed = this.speedFromState();
        deltaSec = Math.max((note.time * 0.001) / speed, MIN_DELAY_SEC);
      }
      this.store.set({ playEvent: nextEvent });
    }

    return {
      note,
      varIdx: playVar,
      barIdx: playBar,
      eventIdx: playEvent,
      deltaSec,
      variationChanged,
    };
  }
}
